import { service } from '@ember/service';
import Component from '@glimmer/component';
import IdNameMismatchCard from 'garaje/components/approvals/screeners/id-name-mismatch-card';
import type ScreenerResult from 'garaje/models/screener-result';
import type { IdScanningFaceMatch, IdScanningNameMatch, IdScanningReport } from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type IdNameReview from 'garaje/utils/id-name-review';

interface IdScanningSignature {
  Args: {
    result: ScreenerResult<IdScanningReport>;
    model: GuestWithVisitApproval;
    nameReview: IdNameReview;
  };
  Blocks: {
    default: [];
  };
}

export default class IdScanning extends Component<IdScanningSignature> {
  @service declare featureFlags: FeatureFlagsService;

  get report(): IdScanningReport {
    return this.args.result.report;
  }

  get nameMatch(): IdScanningNameMatch | undefined {
    return this.report?.['name-match'];
  }

  get faceMatch(): IdScanningFaceMatch | undefined {
    return this.report?.['face-match'];
  }

  /**
   * Whether to render the editable name-mismatch card. Gated on the
   * `id-name-mismatch-screener` split and the report flagging a name mismatch.
   */
  get isNameMismatch(): boolean {
    return this.featureFlags.isEnabled('id-name-mismatch-screener') && Boolean(this.nameMatch?.mismatch);
  }

  /**
   * Whether the face check held this visit. `held` is stamped by the backend
   * screener — the policy (which verdicts/reasons hold) lives there, not here.
   */
  get isFaceHeld(): boolean {
    return this.featureFlags.isEnabled('id-face-match-screener') && Boolean(this.faceMatch?.held);
  }

  get showVerificationCard(): boolean {
    return this.isNameMismatch || this.isFaceHeld;
  }

  /**
   * The name that was compared against the ID: the profile's stored legal name
   * when one exists (`comparison-left`), otherwise the typed entry/invite name.
   * Mirrors the mismatch email's REGISTERED NAME.
   */
  get registeredName(): string {
    return this.nameMatch?.['comparison-left'] || this.args.model.fullName;
  }

  <template>
    {{#if @model.visitApproval.idScanningPendingResult}}
      <li class="text-sm mt-1 sm:mt-4" data-test-id-scanning-pending>
        <img src="/assets/images/progress-small-gray.svg" alt="" role="presentation" />&nbsp;
        <span class="whitespace-nowrap">
          Visitor’s ID is processing.
        </span>
      </li>
    {{else if this.showVerificationCard}}
      <IdNameMismatchCard
        @nameMatch={{if this.isNameMismatch this.nameMatch}}
        @faceMatch={{if this.isFaceHeld this.faceMatch}}
        @registeredName={{this.registeredName}}
        @nameReview={{@nameReview}}
      />
    {{else if @model.visitApproval.idScanningReviewResult}}
      <li class="text-sm mt-1 sm:mt-4">{{@result.idScanningFailureMessage}}</li>
    {{/if}}
    {{yield}}
  </template>
}
